import {
    Box,
    Heading,
    Input,
    Button,
    Spinner,
    Text,
    Flex,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";

import { searchPartsByVin } from "../Api/vinSearch.service";
import PartsGrid from "../components/PartsGrid";
import PartsFilters, {
    PartsFilterState,
} from "../components/PartsFilters";
import { applyPartFilters } from "../utils/applyPartFilters";
import { Part } from "../Types/part";
import { useVehicle } from "../../contexts/VehicleContext";

const VinSearchPage = () => {
    const [vin, setVin] = useState("");
    const [loading, setLoading] = useState(false);
    const [parts, setParts] = useState<Part[]>([]);
    const [error, setError] = useState("");

    const { vehicle, setVehicle } = useVehicle();

    const [filters, setFilters] = useState<PartsFilterState>({
        oem: "all",
        inStockOnly: false,
    });

    const handleSearch = async () => {
        if (!vin) return;

        try {
            setLoading(true);
            setError("");

            const result = await searchPartsByVin(vin.trim().toUpperCase());

            setVehicle(result.vehicle);
            setParts(result.parts);
        } catch {
            setError("Invalid VIN or no compatible parts found.");
            setParts([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredParts = useMemo(
        () => applyPartFilters(parts, filters),
        [parts, filters]
    );

    return (
        <Box>
            <Heading mb={4}>Search Parts by VIN</Heading>

            <Flex gap={3} maxW="500px" >
                <Input
                    border={'1px solid #a3a3a3ff'}
                    _placeholder={{ fontWeight: '500', color: 'gray.400' }}
                    color="gray.700"
                    _hover={{ borderColor: 'teal.400' }}
                    _focus={{ borderColor: 'teal.500', boxShadow: '0 0 0 0px teal' }}
                    name="seatsAvailable"
                    placeholder="Enter VIN"
                    value={vin}
                    onChange={(e) => setVin(e.target.value)}
                />
                <Button colorScheme="teal" onClick={handleSearch}>
                    Search
                </Button>
            </Flex>

            {loading && <Spinner mt={4} />}

            {error && (
                <Text mt={4} color="red.500">
                    {error}
                </Text>
            )}

            {vehicle && (
                <Box mt={6} p={4} borderWidth="1px" borderRadius="md">
                    <Text fontWeight="bold">
                        {vehicle.make} {vehicle.model} ({vehicle.year})
                    </Text>
                    <Text fontSize="sm">Engine: {vehicle.engine}</Text>
                </Box>
            )}

            {parts.length > 0 && (
                <PartsFilters
                    filters={filters}
                    onChange={setFilters}
                />
            )}

            <PartsGrid parts={filteredParts} />
        </Box>
    );
};

export default VinSearchPage;
